import { Loader } from '../../Widgets';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import ArthaGateway from 'service/ArthaGateway';
import { Party, SaleOrder, Product, OrderItem, OrderDocument, Account, Receipt } from 'service';
import defaultImage from "../../../assets/default.png"
import { faChevronLeft, faGripVertical, faTableList, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast, { Toaster } from 'react-hot-toast';

type PageError = {
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
}

type ErrorBoundaryState = {
    hasError: boolean;
}

class NewInvoiceErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        console.error('NewInvoice render error:', error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="invoice-error-shell">
                    <div className="invoice-error-card">
                        <h2>Something went wrong</h2>
                        <p>We could not render this screen. Please reload this page and try again.</p>
                        <div className="btn btn-dark" onClick={() => window.location.reload()}>Reload Page</div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export const NewInvoice = () => {
    const appData = useSelector((state: RootState) => state.appdata);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [screenMode, setScreenMode] = useState<string>("main-screen");
    const [user, setUser] = useState<Party>();
    const [party, setParty] = useState<Party>();
    const [productGroups, setProductGroups] = useState<any[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [productGroup, setProductGroup] = useState<any>();
    const [productName, setproductName] = useState<string>("");
    const [selectionMode, setSelectionMode] = useState<string>("table");
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [totalPages, setTotalPages] = useState<number>(5);
    const [order, setOrder] = useState<SaleOrder>(new SaleOrder());
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [receipt, setReceipt] = useState<Receipt>(new Receipt());
    const [pageError, setPageError] = useState<PageError | null>(null);
    const paymentTypes = ["Cash", "UPI", "Cheque", "NEFT", "RTGS", "IMPS"];
    const [quantitySet, setQuantitySet] = useState<number[]>(Array.from(Array(20).keys()));

    const navigate = useNavigate();
    const handleFocus = (event: any) => event.target.select();

    const getErrorMessage = (error: any, fallback: string) => {
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }
        if (error?.message) {
            return error.message;
        }
        return fallback;
    }

    useEffect(() => {
        if (appData.party) {
            setParty({ ...appData.party });
            setIsLoading(false);
            setPageError(null);
            loadProductGroups();
        } else {
            setIsLoading(false);
            setPageError({
                title: 'No customer selected',
                message: 'Please select a customer before creating a new order.',
                actionLabel: 'Go To Customers',
                onAction: () => navigate('/customers')
            });
        }
    }, [appData, navigate]);

    useEffect(() => {
        if (party) {
            setOrder((prevOrder) => ({
                ...prevOrder,
                shippingStateCode: party.billingStateCode,
                shippingAddress1: party.billingAddress1,
                shippingAddress2: party.billingAddress2,
                shippingCity: party.billingCity,
                shippingPinCode: party.billingPinCode,
                party: party
            }));
            loadProductGroups();
        }
    }, [party]);

    useEffect(() => {
        loadProducts();
    }, [pageNumber]);

    useEffect(() => {
        loadPartyDetails();
        let value = localStorage.getItem("selectionMode");
        if (value) {
            setSelectionMode(value);
        }
    }, []);

    useEffect(() => {
        setProducts([]);
        if (pageNumber != 1) {
            setPageNumber(1);
        }
        else {
            loadProducts();
        }
    }, [productGroup, productName]);

    const loadBankAccounts = () => {
        let cache_key = "bank_accounts_" + appData.companyId;
        if (sessionStorage.getItem(cache_key) !== null) {
            let localData = sessionStorage.getItem(cache_key);
            if (localData) {
                let cachedAccounts = JSON.parse(localData);
                setAccounts(cachedAccounts);
                initReceipt(cachedAccounts);
            }
        }
        else {
            ArthaGateway
                .getAccounts("", 2)
                .then((data) => {
                    setAccounts(data.entries);
                    sessionStorage.setItem(cache_key, JSON.stringify(data.entries));
                    initReceipt(data.entries || []);
                })
                .catch(error => {
                    console.log(error);
                    setPageError({
                        title: 'Unable to load payment accounts',
                        message: getErrorMessage(error, 'Please try again in a moment.')
                    });
                    setIsLoading(false);
                });
        }
    }

    const initReceipt = (availableAccounts: Account[] = []) => {
        let receipt = new Receipt();
        receipt.date = moment().format("YYYY-MM-DD");
        receipt.series = "2025-2026";
        receipt.transactionType = "Receipt";
        receipt.paymentType = "Cash";
        receipt.incomeAccountId = 14;
        receipt.accountId = (availableAccounts.length > 0) ? availableAccounts[0].id : 6; // Default to Cash Account
        receipt.incomeAccount = { id: 14, name: "Sales" };
        receipt.amount = 0;
        setReceipt(receipt);
    }

    const loadProducts = () => {
        if (!productGroup) {
            return;
        }
        setIsProductsLoading(true);
        ArthaGateway
            .getProducts(productGroup.id, productName, pageNumber, pageSize)
            .then((data) => {
                if (pageNumber == 1) {
                    setProducts(data.entries);

                    if (screenMode === "main-screen" && order.items.length === 0) {
                        setScreenMode("select-product");
                    }
                }
                else {
                    setProducts((prevProducts) => prevProducts.concat(data.entries));
                }
                setTotalPages(data.totalPages);
                setIsProductsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsProductsLoading(false);
                setPageError({
                    title: 'Unable to load products',
                    message: getErrorMessage(error, 'Please check your connection and try again.'),
                    actionLabel: 'Retry',
                    onAction: () => loadProducts()
                });
            });
    }


    const loadProductGroups = () => {
        if (productGroups.length > 0) {
            loadBankAccounts();
            return;
        }
        if (sessionStorage.getItem("catagories") === null) {
            ArthaGateway
                .getProductGroups()
                .then((data) => {
                    data = data.filter((g: any) => g.id !== 999);
                    data.forEach((element: any) => {
                        element.products = [];
                    });
                    data.unshift({ id: 0, name: "All" });
                    sessionStorage.setItem("catagories", JSON.stringify(data));
                    setProductGroups(data);
                    setProductGroup(data[0]);
                    setPageError(null);
                    loadBankAccounts();
                })
                .catch(error => {
                    console.log(error);
                    setPageError({
                        title: 'Unable to load categories',
                        message: getErrorMessage(error, 'Please refresh and try again.'),
                        actionLabel: 'Retry',
                        onAction: () => loadProductGroups()
                    });
                    setIsLoading(false);
                });
        }
        else {
            let localData = sessionStorage.getItem("catagories");
            if (localData) {
                let catagories = JSON.parse(localData);
                setProductGroups(catagories);
                setProductGroup(catagories[0]);
                setPageError(null);
                loadBankAccounts();
            }
        }
    }

    const loadPartyDetails = () => {
    }

    const selectProduct = (product: Product) => {
        localStorage.setItem("selectionMode", selectionMode);
        addProduct(product);
    }

    const removeCartItem = (index: number) => {
        const items = [...order.items];
        items.splice(index, 1);
        const updatedOrder = { ...order, items };
        calculateOrderTotal(updatedOrder);
        setOrder(updatedOrder);
    }

    const addProduct = (product: Product) => {
        let item: OrderItem = new OrderItem();
        item.product = { ...product };
        item.quantity = 1;
        setCalculatedValues(item, order);

        const items = [...order.items, item].sort((a, b) => a.product.code.localeCompare(b.product.code));
        const updatedOrder = { ...order, items };

        calculateOrderTotal(updatedOrder);

        setOrder(updatedOrder);
        setScreenMode("main-screen");
    }

    const setCalculatedValues = (item: OrderItem, order: SaleOrder) => {
        let grossValue = (item.product.price * item.quantity);
        item.discount = 0;
        if (item.product.discount > 0) {
            item.discount = (grossValue) * (item.product.discount / 100);
        }
        item.taxableValue = grossValue - item.discount;
        let tax = item.taxableValue * (item.product.taxPercentage / 100);
        item.cgstTax = (tax / 2);
        item.sgstTax = (tax / 2);
        item.igstTax = 0;
        item.netValue = item.taxableValue + item.cgstTax + item.sgstTax + item.igstTax;
    }

    const calculateOrderTotal = (order: SaleOrder) => {
        order.taxableValue = order.items.reduce((a, b) => a + b.taxableValue, 0);
        order.discount = order.items.reduce((a, b) => a + b.discount, 0);
        order.cgstTax = order.items.reduce((a, b) => a + b.cgstTax, 0);
        order.sgstTax = order.items.reduce((a, b) => a + b.sgstTax, 0);
        order.igstTax = order.items.reduce((a, b) => a + b.igstTax, 0);
        order.netValue = order.items.reduce((a, b) => a + b.netValue, 0);
        order.roundOff = order.netValue - Math.round(order.netValue);
        order.billAmount = Math.round(order.netValue);
    }

    const updateQuantity = (e: any, index: number) => {
        let quantity = e.target.value;
        if (e.target.value.length === 0) {
            quantity = 0;
        }
        let item = order.items[index];
        item.quantity = parseFloat(quantity);
        setCalculatedValues(item, order);
        calculateOrderTotal(order);
        setOrder({ ...order, items: [...order.items] });
    }

    const updatePrice = (e: any, index: number) => {
        let price = e.target.value;
        if (e.target.value.length === 0) {
            price = 0;
        }
        let item = order.items[index];
        item.product.price = parseFloat(price);
        setCalculatedValues(item, order);
        calculateOrderTotal(order);
        setOrder({ ...order, items: [...order.items] });
    }

    const updateOrderNotes = (e: any) => {
        setOrder({ ...order, notes: e.target.value });
    }

    const saveOrder = () => {
        if (order.items.length === 0) {
            toast('Please add products to the order', { position: 'bottom-center', className: 'btn bg-danger' });
            return;
        }

        order.date = moment().format("YYYY-MM-DD");
        order.dueDate = moment().add(15, 'days').format("YYYY-MM-DD");
        order.series = "2025-2026";
        order.status = 1;
        ArthaGateway
            .saveInvoice(order)
            .then((data) => {
                toast('Invoice saved successfully', { position: 'bottom-center', className: 'btn bg-success' });
                setTimeout(() => {
                    navigate("/invoice/" + data);
                }, 2000);
            })
            .catch(error => {
                console.log(error);
                setPageError({
                    title: 'Unable to save order',
                    message: getErrorMessage(error, 'Please try again after a moment.')
                });
                toast.error('Unable to save order right now');
            });
    }

    const showConfirmOrder = () => {
        if (order.items.length === 0) {
            toast('Please add products to the order', { position: 'bottom-center', className: 'btn bg-danger' });
            return;
        }

        if (receipt.amount > 0) {
            const preparedReceipt = { ...receipt, party: order.party };
            setOrder({ ...order, receipts: [preparedReceipt] });
        }

        setScreenMode("confirm-order");
    }

    const currencyFormat = (value: number) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <NewInvoiceErrorBoundary>
            <div className='page page-new-order app-page-shell'>
            {isLoading &&
                <Loader />
            }
            {!isLoading &&
                <>
                    <div><Toaster /></div>
                    {pageError &&
                        <div className='invoice-inline-error mb-3'>
                            <h5>{pageError.title}</h5>
                            <p>{pageError.message}</p>
                            {pageError.onAction &&
                                <div className='btn btn-dark btn-sm' onClick={pageError.onAction}>{pageError.actionLabel || 'Try Again'}</div>
                            }
                        </div>
                    }
                    {screenMode === "main-screen" &&
                        <div className='invoice-shell'>
                            <div className='invoice-page-title'>
                                <h2>Create New Order</h2>
                                <div className='invoice-page-subtitle'>Fast checkout with editable pricing, tax and advance details.</div>
                            </div>
                            {order.party &&
                                <>
                                    <div className='row'>
                                        <div className='bg-light mb-2 p-4 invoice-customer-card' style={{ paddingBottom: "20px" }}>
                                            <i>Customer:</i>
                                            <h2 style={{ marginBottom: 0 }}>{party?.name}</h2>
                                            {party?.mobileNumber}
                                        </div>
                                    </div>
                                    {order.items &&
                                        <div className="row">
                                            <div className='cart-items'>
                                                <div className='d-flex justify-content-between align-items-center mb-2'>
                                                    <div className='cart-section-title'>Items ({order.items.length})</div>
                                                    {order.items.length > 0 &&
                                                        <div className="form-check form-switch">
                                                            <label className="form-check-label">Show details</label>
                                                            <input className="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                checked={showDetails}
                                                                onChange={(e) => setShowDetails(e.target.checked)} />
                                                        </div>
                                                    }
                                                </div>
                                                {order.items.length == 0 &&
                                                    <div className="app-empty-state">
                                                        Your cart is empty. Add products to start this invoice.
                                                    </div>
                                                }
                                                {order.items.map((item, index) => (
                                                    <div className='cart-item-card' key={index}>
                                                        <div className='d-flex justify-content-between align-items-start gap-2'>
                                                            <div>
                                                                <div className='cart-item-name'>{item.product.name}</div>
                                                            </div>
                                                            <button
                                                                type='button'
                                                                className="btn btn-outline-danger btn-sm cart-remove-btn"
                                                                title='Remove Item'
                                                                onClick={() => removeCartItem(index)}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                        <div className="row g-2 align-items-end mt-1">
                                                            <div className="col-6">
                                                                <label className='form-label mb-1'>Qty</label>
                                                                <input type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={item.quantity}
                                                                    onChange={(e) => updateQuantity(e, index)}
                                                                    onFocus={handleFocus} />
                                                            </div>
                                                            <div className="col-6">
                                                                <label className='form-label mb-1'>Rate</label>
                                                                <div className='input-group input-group-sm'>
                                                                    <input type="number"
                                                                        className="form-control"
                                                                        value={item.product.price}
                                                                        onChange={(e) => updatePrice(e, index)}
                                                                        onFocus={handleFocus} />
                                                                    <span className='input-group-text'>/{item.product.unit}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex justify-content-between align-items-center mt-2'>
                                                            <span className='cart-label'>Line total</span>
                                                            <span className='cart-item-total'>{currencyFormat(item.netValue)}</span>
                                                        </div>
                                                        {showDetails &&
                                                            <div className='cart-item-breakup mt-2'>
                                                                {item.discount > 0 &&
                                                                    <div className='d-flex justify-content-between'>
                                                                        <span>Discount @ {item.product.discount}%</span>
                                                                        <span>{currencyFormat(item.discount)}</span>
                                                                    </div>
                                                                }
                                                                <div className='d-flex justify-content-between'>
                                                                    <span>Taxable Value</span>
                                                                    <span>{currencyFormat(item.taxableValue)}</span>
                                                                </div>
                                                                <div className='d-flex justify-content-between'>
                                                                    <span>CGST @ {(item.product.taxPercentage / 2)}%</span>
                                                                    <span>{currencyFormat(item.cgstTax)}</span>
                                                                </div>
                                                                <div className='d-flex justify-content-between'>
                                                                    <span>SGST @ {(item.product.taxPercentage / 2)}%</span>
                                                                    <span>{currencyFormat(item.sgstTax)}</span>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                ))}
                                                {order.items.length > 0 &&
                                                    <div className='cart-grand-total'>
                                                        <div className='cart-label'>Grand Total</div>
                                                        <h2>{currencyFormat(order.billAmount)}</h2>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    }
                                    <div className="row">
                                        <div className='col'>
                                            <div className="btn btn-dark btn-sm w-50 mt-2 mb-2" onClick={() => setScreenMode("select-product")}>Add another product</div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className='form-group'>
                                            <label>Notes</label>
                                            <textarea
                                                className="form-control"
                                                rows={3}
                                                value={order.notes}
                                                onChange={(e) => updateOrderNotes(e)}
                                                style={{ paddingTop: "1.5rem" }}>
                                            </textarea>
                                        </div>
                                    </div>
                                    <div className="row justify-content-center mt-2">
                                        <div className='col-12 col-md-4'>
                                            <div className="form-group">
                                                <label>Payment Type</label>
                                                <select className="form-select" value={receipt.paymentType} onChange={(e) => setReceipt({ ...receipt, paymentType: e.target.value })}>
                                                    {paymentTypes.map((paymentType, index) => (
                                                        <option key={index} value={paymentType}>{paymentType}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className='col-12 col-md-4'>
                                            <div className="form-group">
                                                <label>Account</label>
                                                <select className="form-select" value={receipt.accountId} onChange={(e) => setReceipt({ ...receipt, accountId: parseInt(e.target.value) })}>
                                                    <option value="0">Select Account</option>
                                                    {accounts.map((account, index) => (
                                                        <option key={index} value={account.id}>{account.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className='col-12 col-md-4'>
                                            <div className="form-group">
                                                <label>Advance Amount</label>
                                                <input type="number" className="form-control" placeholder="Amount" value={receipt.amount} onChange={(e) => setReceipt({ ...receipt, amount: parseInt(e.target.value) })} />
                                            </div>
                                        </div>
                                    </div>
                                    {order.items.length > 0 &&
                                        <div className="row">
                                            <div className="col">
                                                <div className="btn btn-dark w-100 mt-3 invoice-primary-btn" title="Save" onClick={() => showConfirmOrder()}>Create Order</div>
                                            </div>
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    }
                    {screenMode === "select-product" &&
                        <div className='invoice-shell'>
                            <div className='app-page-header'>
                                <h2>Select Products</h2>
                                <p>Filter by category or name and add items to the cart.</p>
                            </div>
                            <div className='app-panel invoice-product-filters mb-3'>
                                <div className='row g-2'>
                                    {productGroups.length > 0 &&
                                        <div className="col-12 col-md-5">
                                            <label className='form-label'>Category</label>
                                            <select
                                                className='form-select'
                                                value={productGroup?.id}
                                                onChange={(e) => setProductGroup(productGroups.find(d => d.id === parseInt(e.target.value)))}>
                                                {productGroups.map((group, index) => {
                                                    return (
                                                        <option key={index} value={group.id}>{group.name}</option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    }
                                    <div className="col-12 col-md-5">
                                        <label className='form-label'>Name</label>
                                        <input
                                            className='form-control'
                                            placeholder='Search by product name'
                                            value={productName}
                                            onChange={(e) => setproductName(e.target.value)} />
                                    </div>
                                    <div className="col-12 col-md-2">
                                        <label className='form-label d-none d-md-block'>&nbsp;</label>
                                        <div className='btn-group w-100'>
                                            <button
                                                type='button'
                                                className={"btn btn-sm " + ((selectionMode === "grid") ? "btn-dark" : "btn-outline-dark")}
                                                onClick={() => setSelectionMode("grid")}
                                                title='Grid View'>
                                                <FontAwesomeIcon icon={faGripVertical} />
                                            </button>
                                            <button
                                                type='button'
                                                className={"btn btn-sm " + ((selectionMode === "table") ? "btn-dark" : "btn-outline-dark")}
                                                onClick={() => setSelectionMode("table")}
                                                title='List View'>
                                                <FontAwesomeIcon icon={faTableList} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="app-panel invoice-product-results">
                                <div className='d-flex justify-content-between align-items-center mb-2'>
                                    <div className='cart-section-title'>Products ({products.length})</div>
                                    {isProductsLoading &&
                                        <div className="spinner-border spinner-border-sm text-dark" role="status" />
                                    }
                                </div>
                                {selectionMode === "table" &&
                                    <div className="products row g-0">
                                        <table className="table table-hover align-middle products-table mb-0">
                                            <tbody>
                                                {products.map((product, index) => (
                                                    <tr key={index} onClick={() => selectProduct(product)} className='invoice-product-row'>
                                                        <td>
                                                            <div className='fw-bold'>{product.name}</div>
                                                            {product.price > 0 &&
                                                                <div className='text-muted small'>{currencyFormat(product.price)}/{product.unit}</div>
                                                            }
                                                        </td>
                                                        <td className='text-end'>
                                                            <button type='button' className="btn btn-dark btn-sm">Select</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                }
                                {selectionMode === "grid" &&
                                    <div className="products row">
                                        {products.map((product, index) => (
                                            <div key={index} className="col-6">
                                                <div className="card mt-2 product-pick-card">
                                                    <img className="card-img-top" src={(product.imageUrl && product.imageUrl.length > 0) ? product.imageUrl : defaultImage} alt="product" />
                                                    <div className="card-body d-flex flex-column">
                                                        <div className="card-title">{product.name}</div>
                                                        <div className='card-text'>
                                                            {currencyFormat(product.price)}/{product.unit}
                                                        </div>
                                                        <button type='button' className="btn btn-dark btn-sm w-100 mt-auto" onClick={() => selectProduct(product)}>Select</button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                }
                                {!isProductsLoading && products.length === 0 &&
                                    <div className='app-empty-state'>
                                        No products found for this filter.
                                    </div>
                                }
                                {(totalPages > pageNumber) &&
                                    <>
                                        {isProductsLoading &&
                                            <div className="btn btn-dark w-100 text-center mt-2">
                                                <div className="spinner-border text-light" role="status">
                                                </div>
                                            </div>
                                        }
                                        {!isProductsLoading &&
                                            <div className="btn btn-outline-dark w-100 text-center mt-2" onClick={() => setPageNumber(pageNumber + 1)}>Load More...</div>
                                        }
                                    </>
                                }
                            </div>
                            <button type='button' className='btn btn-outline-dark w-100 mt-2' onClick={() => setScreenMode("main-screen")}>
                                <FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: 10 }} />Back to order
                            </button>
                        </div>
                    }
                    {screenMode === "confirm-order" &&
                        <div className='invoice-shell'>
                            <div className='app-page-header'>
                                <h2>Confirm Order</h2>
                                <p>Review items, notes, and advance before final confirmation.</p>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <div className="alert alert-secondary">
                                        <b>{order.party?.name}</b><br />
                                        {order.party?.billingAddress1}<br />
                                        {order.party?.billingAddress2}<br />
                                        {order.party?.billingCity} - {order.party?.billingPinCode}
                                    </div>
                                </div>
                            </div>
                            {order.items &&
                                <div className="row">
                                    <div className="cart-items">
                                        <div className="d-flex justify-content-end mb-2">
                                            <div className="form-check form-switch">
                                                <label className="form-check-label">Show details</label>
                                                <input className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={showDetails}
                                                    onChange={(e) => setShowDetails(e.target.checked)} />
                                            </div>
                                        </div>
                                        {order.items.map((item, index) => (
                                            <div className='cart-item-card' key={index}>
                                                <div className='d-flex justify-content-between align-items-center'>
                                                    <div className="cart-item-name">
                                                        {item.product.name}
                                                    </div>
                                                    <div className='cart-item-total'>{currencyFormat(item.netValue)}</div>
                                                </div>
                                                <div className='cart-item-unit mt-1'>
                                                    {item.quantity} x {item.product.price}/{item.product.unit}
                                                </div>

                                                {showDetails &&
                                                    <div className='cart-item-breakup mt-2'>
                                                        {item.discount > 0 &&
                                                            <div className='d-flex justify-content-between'>
                                                                <span>Discount @ {item.product.discount}%</span>
                                                                <span>{currencyFormat(item.discount)}</span>
                                                            </div>
                                                        }
                                                        <div className='d-flex justify-content-between'>
                                                            <span>Taxable Value</span>
                                                            <span>{currencyFormat(item.taxableValue)}</span>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        ))}
                                        {order.items.length > 0 &&
                                            <div className='cart-grand-total'>
                                                <div className='cart-label'>Grand Total</div>
                                                <h2>{currencyFormat(order.billAmount)}</h2>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                            {order.notes && order.notes.length > 0 &&
                                <div className="row">
                                    <div className="alert alert-secondary">
                                        <b>Notes:</b><br />
                                        {order.notes}
                                    </div>
                                </div>
                            }
                            <div className="row">
                                <ul className="list-group list-group-sm cart-items">
                                    <li className='list-group-item'>
                                        <div className='row'>
                                            <div className="col-12 text-center">
                                                <h3>Advance</h3>
                                            </div>
                                        </div>
                                    </li>
                                    {(!order.receipts || order.receipts.length === 0) &&
                                        <li className='list-group-item'>
                                            <div className='row'>
                                                <div className="col-12 text-center">
                                                    No advance received
                                                </div>
                                            </div>
                                        </li>
                                    }
                                    {(order.receipts || []).map((receipt, index) => (
                                        <li className='list-group-item'>
                                            <div className="row">
                                                <div className="col-8">
                                                    {receipt.paymentType}
                                                    &nbsp;
                                                    <i>{receipt.referenceNumber}</i>
                                                </div>
                                                <div className="col-4" style={{ textAlign: 'right' }}>
                                                    {currencyFormat(receipt.amount)}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <div className="btn btn-dark w-100 mb-2 mt-2" title="Save" onClick={() => saveOrder()}>Confirm Sale</div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <div className="btn btn-sm btn-danger w-100" title="Back" onClick={() => setScreenMode("main-screen")}>Back</div>
                                </div>
                            </div>
                        </div>
                    }
                </>
            }
            </div >
        </NewInvoiceErrorBoundary>
    );
};